import { forwardRef, type HTMLAttributes } from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';

import { cn } from '@/lib/cn';
import { initialsOf, readableInkOn, toCssHex } from '@/lib/format';

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  name: string;
  /** Profile tint. The API stores it bare ("6366F1"); a leading # is accepted too. */
  colorHex: string;
  size?: 'sm' | 'md' | 'lg';
  /** Optional photo. Falls back to initials if it fails to load. */
  src?: string;
}

const SIZES = {
  sm: 'size-8 text-xs',
  md: 'size-10 text-sm',
  lg: 'size-14 text-lg',
} as const;

/**
 * Initials on the profile's colour. There are no uploaded photos in v1, so the
 * colour is the only thing distinguishing two profiles at a glance — which is
 * why the foreground is computed for contrast rather than hardcoded white.
 */
export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(function Avatar(
  { name, colorHex, size = 'md', src, className, ...props },
  ref,
) {
  const background = toCssHex(colorHex);
  const foreground = readableInkOn(background);

  return (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        'relative inline-flex shrink-0 select-none items-center justify-center',
        'overflow-hidden rounded-full ring-1 ring-inset ring-black/10',
        SIZES[size],
        className,
      )}
      {...props}
    >
      {src ? (
        <AvatarPrimitive.Image
          src={src}
          alt=""
          className="size-full object-cover"
        />
      ) : null}
      <AvatarPrimitive.Fallback
        // No delay: on a LAN the fallback flashing in is fine, but a blank
        // circle while a nonexistent image "loads" is not.
        delayMs={0}
        className="flex size-full items-center justify-center font-semibold"
        style={{ backgroundColor: background, color: foreground }}
      >
        <span aria-hidden="true">{initialsOf(name)}</span>
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
});
